import { StatementRuleTestResult } from "./StatementRuleTestResult";
import { RuleExecutable } from "../../Common/Execution/RuleExecutable";
import { IDesignValidationProvider, ValidationIssue } from "../../Common/IDesignValidationProvider";
import { DataUtilities } from "../../Common/DataUtilities";
import { ExpressionConfiguration } from "../../Expressions/ExpressionConfiguration";
import { IDataDefinition } from "../../Common/Data/Definitions/IDataDefinition";
import { IfThenExpression } from "../../Expressions/Expressions/Logic/IfThenExpression";
import { ExecutionData } from "../../Common/Data/ExecutionData/ExecutionData";
import { ITestingContext } from "../../Common/Testing/ITestingContext";
import { EventLog, EventLogType } from "../../Common/Event/EventLog";
import type { AppGlueAny } from "../../Common/AppGlueAny";

export class StatementRuleConfiguration extends RuleExecutable implements IDesignValidationProvider {
    __id: string = DataUtilities.generateUniqueId();
    expression: ExpressionConfiguration;

    title?: string;

    constructor() {
        super();
        this.expression = new ExpressionConfiguration();
        this.expression.expressionRoot.subExpression = new IfThenExpression();

        this.expression.executionResult = () => {
            return this.getRuleResult();
        };

        this.expression.inputData = () => {
            return this.getInputDefinition();
        };
    }

    get executionResult(): IDataDefinition | (() => IDataDefinition) {
        return this._executionResult!;
    }

    set executionResult(res: (() => IDataDefinition) | IDataDefinition) {
        let executionResult: IDataDefinition | undefined;
        if (res instanceof Function) {
            executionResult = res();
        } else {
            executionResult = res;
        }

        this._executionResult = res;

        this.expression.executionResult = () => {
            return executionResult!;
        };
    }

    run(data: ExecutionData, testContext?: ITestingContext): AppGlueAny {
        let subContext: StatementRuleTestResult | undefined;
        if (testContext) {
            subContext = new StatementRuleTestResult(this.__id);
            testContext.addTestResultItem(subContext);
            subContext.addLogItem(new EventLog("StatementRule: start evaluating"));
        }

        try {
            const res = this.expression.run(data, subContext);

            if (subContext) {
                subContext.result = res !== undefined ? res.toString() : "undefined";
                subContext.addLogItem(new EventLog(`ExpressionDefinition: end evaluating with result [${res}]`));
            }

            return res;
        } catch (e) {
            if (subContext) {
                subContext.addLogItem(new EventLog(e.message, EventLogType.ERROR));
                subContext.isError = true;
                subContext.result = e.message;
            }
            throw e;
        }
    }

    getDesignValidationIssues(): ValidationIssue[] {
        return this.expression.getDesignValidationIssues();
    }

    getStorageData(): object {
        const data = super.getStorageData();

        Reflect.set(data, "__id", this.__id);
        Reflect.set(data, "title", this.title);
        Reflect.set(data, "expression", this.expression.getStorageData());

        return data;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        this.__id = Reflect.get(data, "__id") ?? this.__id;
        this.title = Reflect.get(data, "title");

        const expressionData = Reflect.get(data, "expression");
        if (expressionData) {
            this.expression.setStorageData(expressionData);
            this.expression.executionResult = () => this.getRuleResult();
            this.expression.inputData = () => this.getInputDefinition();
        }
    }
}
